import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { FONT_STACK } from "./mantineTheme";
import { nativeRuntime } from "../platform/native";
import { alpha, colourFor, token } from "./tokens";

/**
 * Cover art, with a deterministic fallback.
 *
 * The worker fills in art for tracks whose cover never resolves, but only after several
 * genuine misses -- so most entries have no artwork row at all for a while, and a tombstoned
 * track from years ago may never get one. Deriving a colour from the title here means every
 * entry still looks like itself: the same track is the same colour on every visit, and two
 * different tracks are not the same grey box.
 *
 * The tile's whole geometry is inline, which is why `size` is required rather than defaulted:
 * a cover with no size was a cover whose size some other file decided. It is a length and not
 * only a number so that a caller can hand over one it cannot compute -- the player's cover is
 * as wide as the card it sits in, which is a percentage of a column, of a grid, of a window.
 */
export function useNativeResource(src: string | null): string | null {
  const runtime = nativeRuntime();
  const nativePath = runtime !== null && src?.startsWith("/api/") === true;
  const [resource, setResource] = useState<{
    path: string;
    id: string;
    url: string | null;
  } | null>(null);

  useEffect(() => {
    if (!nativePath || runtime === null || src === null) return;
    let disposed = false;
    let acquired: string | null = null;
    void runtime
      .acquireResource(src)
      .then((next) => {
        acquired = next.id;
        if (disposed) {
          void runtime.releaseResource(next.id);
        } else {
          setResource({ path: src, ...next });
        }
      })
      .catch(() => {
        if (!disposed) setResource({ path: src, id: "", url: null });
      });
    return () => {
      disposed = true;
      if (acquired !== null) void runtime.releaseResource(acquired);
    };
  }, [nativePath, runtime, src]);

  if (!nativePath) return src;
  return resource?.path === src ? resource.url : null;
}

export function Artwork({
  src,
  seed,
  size,
  radius = "var(--mantine-radius-md)",
  dimmed = false,
  alt,
}: {
  src: string | null;
  seed: string;
  /**
   * The tile's height; the width follows it, so a cover stays square as it shrinks. A number
   * is px. Any CSS length also works, including `cqw` against an ancestor query container.
   */
  size: number | string;
  /** `50%` for a face: a person is not a record sleeve. */
  radius?: string;
  dimmed?: boolean;
  alt?: string;
}) {
  const { t } = useTranslation();
  const resolvedSrc = useNativeResource(src);
  const [image, setImage] = useState({ src: resolvedSrc, failed: false });
  if (image.src !== resolvedSrc) setImage({ src: resolvedSrc, failed: false });

  const style: CSSProperties = {
    borderRadius: radius,
    objectFit: "cover",
    background: "var(--mantine-color-sunken-filled)",
    display: "grid",
    placeItems: "center",
    // The width follows the used height rather than being set beside it: the player's cover
    // is the one child of its card that may give up space, and it stays square while it does.
    height: size,
    width: "auto",
    aspectRatio: 1,
    flex: "0 1 auto",
    minHeight: 0,
    // A cover that has given up space is still clipped to its own box rather than painting
    // over whatever is under it.
    overflow: "clip",
    ...(dimmed && { filter: "grayscale(1)", opacity: 0.55 }),
  };

  if (!resolvedSrc || (image.src === resolvedSrc && image.failed)) {
    return (
      // A test hook, not a style: it is how `StreamList.test.tsx` tells a generated tile from
      // a real cover.
      <img
        className="artwork artwork--generated"
        style={{ ...style, background: colourFor(seed) }}
        src={initialsTile(initials(seed))}
        alt={alt ?? t("artwork.noArtworkFor", { seed })}
        decoding="async"
      />
    );
  }

  return (
    <img
      // The other half of that hook: a real cover carries no `--generated`.
      className="artwork"
      style={style}
      src={resolvedSrc}
      alt={alt ?? seed}
      loading="lazy"
      decoding="async"
      onError={() => setImage({ src: resolvedSrc, failed: true })}
    />
  );
}

/**
 * The initials, drawn as an image rather than as text sitting in a box.
 *
 * A tile with no cover is still a cover: one graphic with one accessible name, and not a pair
 * of letters that select and copy out of the middle of a list. The viewBox is a flat hundred
 * units and the element scales it, so the glyphs are 40% of the tile's width at every size --
 * which is what the container query bought, without the tile having to be a query container.
 *
 * Only the glyphs are drawn here; the fill stays on the element's own background. An SVG
 * loaded through `src` is its own document, so nothing of the page's styling reaches inside
 * it -- which is also why the typeface is named rather than inherited. `FONT_STACK` is the
 * one the rest of the app renders in and holds no webfont, so the letters come out in the
 * same face they always did.
 */
function initialsTile(text: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">` +
    `<text x="50" y="50" text-anchor="middle" dominant-baseline="central"` +
    ` font-family='${FONT_STACK}' font-size="40" font-weight="700" letter-spacing="0.05em"` +
    ` fill="${alpha(token.white, 0.75)}">${text}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Up to two letters from the title.
 *
 * Matching the words rather than splitting on separators is the point. Station titles are
 * `Artist -- Title`, and the dash is usually an em or en dash rather than the hyphen a
 * separator class tends to list; anything not listed survives as a word of its own and gets
 * printed as an initial, which is how covers came to read `D--`. Punctuation is never an
 * initial, so match letters and digits and let everything else be a boundary.
 */
export function initials(seed: string): string {
  const words = seed.match(/[\p{L}\p{N}]+/gu) ?? [];
  if (!words.length) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}
